'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';

import type { CatalogItem } from './header-data';

import Link from '@eliadmin/core/src/components/link';
import { Icon, type IconName } from '@eliadmin/core/src/components/dlz/ui/icon';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCartQueryOpts } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useLocalePathname } from '@eliadmin/core/src/hooks/use-locale-pathname';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { arePathsEqual } from '@eliadmin/core/src/utils/url-utils';

import { CategoriesPanel } from './categories-panel';

type Tab = 'home' | 'categories' | 'cart' | 'favorites' | 'account';

const ITEM =
  'flex w-full flex-col items-center gap-1 rounded-[4px] pb-0.5 pt-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]';

/** Bottom of the sticky site header – the categories panel starts under it. */
function headerBottom() {
  return document.querySelector('[data-site-header]')?.getBoundingClientRect().bottom ?? 0;
}

/**
 * Figma „Mobile-navbar“ (60387:74463) below `lg`: Domov, Kategórie, Košík with the item count,
 * Obľúbené and Môj účet; the current one in brand red with a bold label. „Kategórie“ opens the
 * catalogue between the header and the navbar (non-modal – both stay usable).
 */
export function MobileNavbar({ catalog }: { catalog: CatalogItem[] }) {
  const { t } = useTranslations();
  const pathname = useLocalePathname();
  const signedIn = useCustomerStore(s => Boolean(s.me));
  const { data: cart } = useQuery(useCartQueryOpts());
  const [panelTop, setPanelTop] = useState<number | null>(null);
  const categoriesOpen = panelTop !== null;
  const count = cart?.count ?? 0;

  useEffect(() => setPanelTop(null), [pathname]);

  useEffect(() => {
    if (!categoriesOpen) return;
    // The top bar (from `md`) scrolls away under the sticky row.
    const update = () => setPanelTop(headerBottom());
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [categoriesOpen]);

  const isAt = (...paths: (string | undefined)[]) =>
    paths.some(path => path !== undefined && arePathsEqual(pathname, path));
  const active: Tab | null = categoriesOpen
    ? 'categories'
    : isAt(ROUTES.HOME() ?? '/')
      ? 'home'
      : isAt(ROUTES.CART())
        ? 'cart'
        : isAt(ROUTES.FAVORITES(), ROUTES.FAVORITE_PRODUCTS())
          ? 'favorites'
          : isAt(ROUTES.PROFILE(), ROUTES.CHANGE_PASSWORD(), ROUTES.ORDERS(), ROUTES.SIGN_IN())
            ? 'account'
            : null;

  const tab = (key: Tab, label: string, icon: IconName, badge?: number): ReactNode => (
    <>
      <span className="relative">
        <Icon
          name={icon}
          size={24}
          className={cn(
            '[&_[opacity]]:opacity-20',
            active === key ? 'text-[#D7191F]' : 'text-[#6B7079]',
          )}
        />
        {badge !== undefined ? (
          <span
            aria-hidden="true"
            className="absolute -top-1.5 left-3.5 flex size-[18px] items-center justify-center rounded-full bg-[#D7191F] text-[11px] font-bold leading-none text-white"
          >
            {badge > 99 ? '99+' : badge}
          </span>
        ) : null}
      </span>
      {' '}
      <span
        className={cn(
          'dlz-caption whitespace-nowrap',
          active === key ? 'font-bold text-[#D7191F]' : 'text-[#6B7079]',
        )}
      >
        {label}
      </span>
    </>
  );

  const link = (key: Tab, href: string | undefined, content: ReactNode, label?: string) => (
    <li className="min-w-0 flex-1">
      <Link
        href={href ?? '#'}
        prefetch={false}
        aria-label={label}
        aria-current={active === key ? 'page' : undefined}
        onClick={() => setPanelTop(null)}
        className={ITEM}
      >
        {content}
      </Link>
    </li>
  );

  return (
    <Dialog.Root
      modal={false}
      open={categoriesOpen}
      onOpenChange={open => setPanelTop(open ? headerBottom() : null)}
    >
      <nav
        aria-label={t('text3241')}
        className="fixed inset-x-0 bottom-0 z-40 rounded-t-3xl bg-white px-4 pb-[max(8px,env(safe-area-inset-bottom))] pt-2 shadow-[0_-4px_35px_rgba(0,0,0,0.12)] lg:hidden"
      >
        <ul className="flex">
          {link('home', ROUTES.HOME() ?? '/', tab('home', t('text160'), 'house'))}
          <li className="min-w-0 flex-1">
            <Dialog.Trigger className={ITEM}>
              {tab('categories', t('text176'), 'circles-three')}
            </Dialog.Trigger>
          </li>
          {link(
            'cart',
            ROUTES.CART(),
            tab('cart', t('text343'), 'shopping-cart', count),
            `${count > 99 ? '99+' : count} ${t('text343')}`,
          )}
          {link(
            'favorites',
            signedIn ? ROUTES.FAVORITES() : ROUTES.FAVORITE_PRODUCTS(),
            tab('favorites', t('text516'), 'heart'),
          )}
          {link('account', ROUTES.PROFILE(), tab('account', t('text197'), 'user'))}
        </ul>
      </nav>
      <Dialog.Portal>
        <CategoriesPanel
          catalog={catalog}
          top={panelTop ?? 0}
          onNavigate={() => setPanelTop(null)}
        />
      </Dialog.Portal>
    </Dialog.Root>
  );
}
